import { useEffect, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { exitDelayMs } from '@/shared/lib/animation'
import { useDismissable } from '@/shared/composables/useDismissable'
import { HaebomLogoSolid } from '@/shared/ui/HaebomLogoSolid'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'
import { SidebarItem } from '@/shared/ui/SidebarItem'
import { MessageIcon } from '@/shared/ui/icons/MessageIcon'
import { XCloseIcon } from '@/shared/ui/icons/XCloseIcon'
import { buttonStyles } from '@/shared/ui/Button'
import { MAIN_NAV } from '@/shared/constants/navigation'

export function SidebarDrawer({ onClose }: { onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const [closing, setClosing] = useState(false)

  // 열리면 닫기 버튼으로 초점을 옮긴다 — 그러지 않으면 키보드 사용자는 열린 것을 알 수 없다.
  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  const requestClose = () => {
    setClosing(true)
    window.setTimeout(onClose, exitDelayMs())
  }

  useDismissable(requestClose)

  return (
    <div className={closing ? 'ui-overlay group is-closing' : 'ui-overlay group'}>
      <div className="ui-dimmed ui-overlay__dim animate-dim-in group-[.is-closing]:animate-dim-out" onClick={requestClose} aria-hidden="true" />

      <div className="absolute inset-y-0 right-0 left-7 flex animate-drawer-in flex-col overflow-hidden bg-white-50 pt-3 pb-4 group-[.is-closing]:animate-drawer-out" role="dialog" aria-modal="true" aria-label="메뉴">
        <div className="flex min-h-0 flex-1 shrink-0 grow basis-0 flex-col items-center gap-4">
          <div className="relative h-[46px] w-full shrink-0">
            <Link
              to="/"
              className="absolute top-1/2 left-4 inline-flex -translate-y-1/2"
              aria-label="해봄 홈"
              onClick={requestClose}
            >
              <HaebomLogoSolid height={28} />
            </Link>
            <button
              ref={closeRef}
              type="button"
              className="ui-pressable ui-pressable--circle absolute top-[7px] right-2 inline-flex h-8 w-8 cursor-pointer items-center justify-center border-0 bg-none p-0 text-black-600"
              aria-label="메뉴 닫기"
              onClick={requestClose}
            >
              <XCloseIcon />
            </button>
          </div>

          <div className="flex w-[315px] max-w-full flex-col gap-3.5">
            <nav aria-label="주요 메뉴">
              <ul className="m-0 flex list-none flex-col gap-4 px-2">
                {MAIN_NAV.map(({ to, label, icon: Icon, end, chevron }) => (
                  <li key={to}>
                    <NavLink to={to} end={end} className="no-underline" onClick={requestClose}>
                      <SidebarItem icon={<Icon />} {...(chevron ? { chevron: <ChevronRightIcon /> } : {})}>
                        {label}
                      </SidebarItem>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            <span className="ui-divider-h" aria-hidden="true" />

            <div className="flex flex-col gap-3 px-2">
              <Link
                to="/problems/new"
                className={buttonStyles('md', 'primary', 'w-full')}
                onClick={requestClose}
              >
                <MessageIcon size={20} />
                우리 동네 문제 등록하기
              </Link>
              <Link
                to="/orgs/new"
                className={buttonStyles('md', 'assistive', 'w-full')}
                onClick={requestClose}
              >
                <CityIcon size={20} />
                기업 등록하기
              </Link>
            </div>
          </div>
        </div>

        <footer className="flex shrink-0 flex-col items-center gap-1">
          <p className="m-0 flex items-center gap-1.5">
            <span className="text-label font-medium text-black-300">소셜 리빙랩 해봄</span>
            <span className="ui-dot" aria-hidden="true" />
            <span className="text-caption text-black-100">광명 지역 문제해결 연결 플랫폼</span>
          </p>
          <p className="m-0 flex items-center gap-1">
            <span className="inline-flex h-[30px] items-center justify-center text-label font-medium text-black-100 no-underline opacity-50">이용 안내</span>
            <span className="ui-divider-v" aria-hidden="true" />
            <Link className="inline-flex h-[30px] items-center justify-center text-label font-medium text-black-100 no-underline" to="/privacy" onClick={requestClose}>
              개인정보처리방침
            </Link>
            <span className="ui-divider-v" aria-hidden="true" />
            {/* 관리자는 라우터가 따로다(데이터 라우터) — 문서를 새로 열어야 그 라우트에 닿는다. */}
            <a className="inline-flex h-[30px] items-center justify-center text-label font-medium text-black-100 no-underline" href="/admin/login">
              관리자
            </a>
          </p>
        </footer>
      </div>
    </div>
  )
}
